import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import {
  selectedProjectTabPageSize,
  SELECTED_PROJECT_TABS,
  encodeProjectsParams
} from '../constants';
import SearchResultsTabEntry from './SearchResultsTabEntry';
import Pagination from '../../../components/navigation/Pagination';
import useHistory from '../../../hooks/useHistory';
import { paths } from '../../../utils/constants/routes';
import { useStore } from '../../../store/store';
import { observable, runInAction } from 'mobx';
import agentSearchSortOptions from '../../search/SearchResults/constants/agentSearchSortOptions';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../components/buttons/IconButton';

export const observableAgentProjectResultsSorts = observable({
  selectedSort: 'default'
});

const SearchResultsTabView = observer(
  ({
    project = {},
    results = [],
    forTab = SELECTED_PROJECT_TABS.RESULTS,
    params = {},
    excludeResult = () => {},
    entriesInAction = {},
    displayMessagePopup
  }) => {
    const { utilsStore } = useStore();
    const { navigate } = useHistory();

    const state = useLocalObservable(() => ({
      params: params,
      desktopActionsWidth: 0,
      setDesktopActionsWidth: (value) => {
        const newWidth = value + 'px';
        if (newWidth !== state.desktopActionsWidth) {
          state.desktopActionsWidth = value + 'px';
        }
      },
      forTab: forTab,
      setForTab: (value = '') => (state.forTab = value),
      selectSortOption: (value = '') => {
        observableAgentProjectResultsSorts.selectedSort = value;
        navigate(
          paths.PROJECTS +
            '?params=' +
            encodeProjectsParams({ ...state.params, selectedProjectTabPage: 1 })
        );
        setTimeout(() => {
          const el = document.getElementById('project-view-scroll-starting-point');
          const header = document.querySelector('header');
          if (el) {
            const headerHeight = header?.clientHeight || 0;
            window.scrollTo({
              top: el.offsetTop - headerHeight,
              behavior: 'smooth'
            });
          }
        }, 100);
      },
      get sortOptions() {
        return agentSearchSortOptions.filter(
          (o) => o.onlyForSearchType === project.searchType || o.onlyForSearchType === 'all'
        );
      },
      get currentResults() {
        const sortedResults =
          state.forTab === SELECTED_PROJECT_TABS.RESULTS
            ? results.filter((r) => !r.isExcluded)
            : results.filter((r) => r.isExcluded);

        if (state.forTab === SELECTED_PROJECT_TABS.RESULTS) {
          if (observableAgentProjectResultsSorts.selectedSort === 'name-asc') {
            sortedResults.sort((a, b) =>
              (a.name || a.companyName || '').localeCompare(b.name || b.companyName || '', 'en', {
                sensitivity: 'base'
              })
            );
          } else if (observableAgentProjectResultsSorts.selectedSort === 'name-desc') {
            sortedResults.sort((a, b) =>
              (b.name || b.companyName || '').localeCompare(a.name || a.companyName || '', 'en', {
                sensitivity: 'base'
              })
            );
          } else if (observableAgentProjectResultsSorts.selectedSort !== 'default') {
            const [propKey, typeSort] = observableAgentProjectResultsSorts.selectedSort.split('-');
            const sorts = {
              asc: (propKey) => (a, b) =>
                +(a[propKey] || Number.MAX_SAFE_INTEGER) - +(b[propKey] || Number.MAX_SAFE_INTEGER),
              desc: (propKey) => (a, b) =>
                +(b[propKey] || -Number.MAX_SAFE_INTEGER / 3) -
                +(a[propKey] || -Number.MAX_SAFE_INTEGER / 3)
            };
            sortedResults.sort(sorts[typeSort](propKey));
          }
        }

        return sortedResults;
      },
      isSortToggled: false,
      toggleSort: () => {
        state.isSortToggled = !state.isSortToggled;
        if (state.isSortToggled) {
          setTimeout(() => {
            const el = document.getElementById('project-view-scroll-starting-point');
            const header = document.querySelector('header');
            if (el) {
              const headerHeight = header?.clientHeight || 0;
              const newTop = el.offsetTop - headerHeight;
              if (window.scrollY < newTop) {
                window.scrollTo({
                  top: newTop,
                  behavior: 'smooth'
                });
              }
            }
          }, 100);
        }
      },
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      }
    }));

    useEffect(() => {
      state.setForTab(forTab);
    }, [state, forTab]);

    useEffect(() => {
      runInAction(() => {
        state.params = params;
      });
    }, [state, params]);

    useEffect(() => {
      const handleClickOutsideSort = (event) => {
        const isClickInsideSortMenu = event.target.closest('.sort-menu-wrap');
        const isClickInsideSortButton = event.target.closest('#sort-btn');

        if (isClickInsideSortMenu || isClickInsideSortButton) {
          // do nothing
        } else {
          if (state.isSortToggled) {
            state.toggleSort();
          }
        }
      };

      document.addEventListener('click', handleClickOutsideSort);

      return () => {
        document.removeEventListener('click', handleClickOutsideSort);
      };
    }, [state]);

    const isFund = project.searchType === 'funds';
    const isPortfolio = project.searchType === 'portfolios';
    const isProfile = project.searchType === 'investprofiles';

    const showPagination = state.currentResults.length > selectedProjectTabPageSize;
    const pages = Math.ceil(state.currentResults.length / selectedProjectTabPageSize) || 1;
    const page = (params.selectedProjectTabPage || 1) - 1;

    return (
      <div
        className="table-responsive"
        style={
          state.isSortToggled ? { minHeight: state.layout === 'desktop' ? '600px' : '660px' } : {}
        }>
        {state.currentResults.length === 0 ? (
          <div className="no-data">No items for this tab</div>
        ) : (
          <>
            <div
              className="header funds-search-cols at-project-results show-also-on-mobile"
              style={
                state.layout === 'mobile'
                  ? {
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-end'
                    }
                  : {}
              }>
              {state.layout === 'desktop' && (
                <>
                  <div className="col name">
                    {isFund
                      ? 'Fund name'
                      : isPortfolio
                        ? 'Portfolio name'
                        : isProfile
                          ? 'Profile name'
                          : 'Entry name'}
                  </div>
                  <div className="col industry-focus">Industry focus</div>
                  <div className="col geo-focus">Geo focus</div>
                  <div className="col enterprise-value">EV (€M)</div>
                  <div className="col equity-value">EQV (€M)</div>
                  <div className="col situation">Situation</div>
                  <div className="col feedback">Feedback results</div>
                </>
              )}
              <div className="col actions funds-search-actions">
                <div className="project-sort-wrapper">
                  <IconButton
                    id="sort-btn"
                    icon={
                      observableAgentProjectResultsSorts.selectedSort !== 'default'
                        ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
                        : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
                    }
                    type={ICON_BUTTON_TYPES.BLUE}
                    tooltipText={
                      state.isSortToggled
                        ? ''
                        : state.currentResults.length
                          ? observableAgentProjectResultsSorts.selectedSort === 'default'
                            ? 'Sort'
                            : agentSearchSortOptions.find(
                                (o) => o.value === observableAgentProjectResultsSorts.selectedSort
                              )?.name || 'Sort'
                          : 'Sort is unavailable at this moment'
                    }
                    tooltipPosition={
                      state.layout === 'mobile' &&
                      observableAgentProjectResultsSorts.selectedSort !== 'default'
                        ? 'bottom-end'
                        : 'bottom-center'
                    }
                    disabled={!state.currentResults.length}
                    onClick={state.toggleSort}
                    filled={
                      state.isSortToggled ||
                      observableAgentProjectResultsSorts.selectedSort !== 'default'
                    }
                  />
                  {state.isSortToggled && (
                    <div className="sort-menu-wrap">
                      {state.sortOptions.map((option) => {
                        return (
                          <IconButton
                            key={option.value}
                            hoverType={ICON_BUTTON_TYPES.BLUE}
                            withBorder={false}
                            innerText={option.shortName || option.name}
                            onClick={() =>
                              state.selectSortOption(option.value) & state.toggleSort()
                            }
                            disabled={
                              observableAgentProjectResultsSorts.selectedSort === option.value
                            }
                            filled={
                              observableAgentProjectResultsSorts.selectedSort === option.value
                            }
                            {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {state.currentResults
              .slice(
                (params.selectedProjectTabPage - 1) * selectedProjectTabPageSize,
                params.selectedProjectTabPage * selectedProjectTabPageSize
              )
              .map((entry, index) => (
                <SearchResultsTabEntry
                  key={entry.id}
                  entry={entry}
                  project={project}
                  type={project.searchType}
                  forTab={forTab}
                  excludeResult={excludeResult}
                  isExcludingResult={entriesInAction[entry.id]}
                  displayMessagePopup={displayMessagePopup}
                  setDesktopActionsWidth={index === 0 ? state.setDesktopActionsWidth : undefined}
                  index={index}
                />
              ))}

            <Pagination
              show={showPagination}
              onPageChange={(selected) => {
                navigate(
                  paths.PROJECTS +
                    '?params=' +
                    encodeProjectsParams({ ...params, selectedProjectTabPage: selected + 1 })
                );
                setTimeout(() => {
                  const el = document.getElementById('project-view-scroll-starting-point');
                  const header = document.querySelector('header');
                  if (el) {
                    const headerHeight = header?.clientHeight || 0;
                    window.scrollTo({
                      top: el.offsetTop - headerHeight,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
              page={page}
              pages={pages}
            />
          </>
        )}
      </div>
    );
  }
);

export default SearchResultsTabView;
